import { TemplateState, } from '@debbie/common/dist/templates/index.js';
import { counted, mapAggregate, notEmpty, } from '@debbie/common/dist/util/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { mapLoadable } from '@debbie/cortex/dist/Loadable/utils.js';
import { useTemplates } from '@debbie/cortex/dist/templates/index.js';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { languageFlags } from './Icons/flags.js';
import { PALETTE } from './SearchableDropdown.js';
import SearchableSelect from './SearchableSelect.js';
// Each language variant is its own row, so the row id carries the language,
// while "recently used" remembers the template whichever language was sent
const rowId = (row) => `${row.shown.templateId}:${row.shown.lang}`;
const rowTemplateId = (row) => row.shown.templateId;
const rowLabel = (row) => row.shown.name;
const rowLang = (row) => row.shown.lang;
const isOffered = (row) => row.offered;
/** The round flag for the language, or its code where no flag exists */
const LanguageIcon = (props) => {
    const Flag = languageFlags[props.lang];
    return Flag ? React.createElement(Flag, { "aria-hidden": "true" }) : React.createElement(React.Fragment, null, props.lang.toUpperCase());
};
const languageIcon = (row) => ({
    icon: React.createElement(LanguageIcon, { lang: row.shown.lang }),
    palette: PALETTE.neutral,
});
/**
 * Browsing all languages offers only the preferred language when the template
 * has it, otherwise every published language so the caseworker can pick one
 */
const toRows = (preferredLang, variants) => {
    const hasPreferred = variants.some((variant) => variant.lang === preferredLang);
    return variants.map((shown) => ({
        shown,
        variants,
        offered: !hasPreferred || shown.lang === preferredLang,
    }));
};
/** The variant that represents a template: preferred, else default, else first */
const representative = (preferredLang, variants) => variants.find((variant) => variant.lang === preferredLang) ??
    variants.find((variant) => variant.isDefaultLang) ??
    variants[0];
/**
 * Searchable single-select for published templates of one type. A language
 * chip narrows the list to that language's variants.
 */
const TemplateSelect = (props) => {
    const lang = useSelector((s) => s.lang);
    const templatesLoadable = useTemplates();
    const templates = h.hasData(templatesLoadable) ? templatesLoadable.data : null;
    const { filter, templateType, preferredLang } = props;
    const eligibleTemplates = useMemo(() => (templates || []).filter((template) => template.type === templateType &&
        template.state === TemplateState.PUBLISHED &&
        (filter ? filter(template) : true)), [templates, templateType, filter]);
    const rows = useMemo(() => mapLoadable(templatesLoadable, Array.from(mapAggregate(eligibleTemplates, (t) => t.templateId).values()).flatMap((variants) => toRows(preferredLang, variants))), [templatesLoadable, eligibleTemplates, preferredLang]);
    // Languages with variants, most common first
    const facet = useMemo(() => {
        const counts = counted(eligibleTemplates, (template) => template.lang);
        return {
            of: rowLang,
            options: Array.from(new Set(eligibleTemplates.map((template) => template.lang)))
                .toSorted((a, b) => counts[b] - counts[a])
                .map((templateLang) => ({
                key: templateLang,
                label: lang.languages[templateLang],
                icon: React.createElement(LanguageIcon, { lang: templateLang }),
            })),
        };
    }, [eligibleTemplates, lang]);
    // The selection may fall outside the current filter (e.g. picked before the
    // case changed) or be unpublished, so it is looked up among every template
    const selectedRow = useMemo(() => {
        if (props.value == null) {
            return null;
        }
        const variants = (templates || []).filter((template) => template.templateId === props.value);
        const published = variants.filter((template) => template.state === TemplateState.PUBLISHED);
        const shown = representative(props.selectedLang ?? preferredLang, published.length > 0 ? published : variants);
        return shown ? { shown, variants, offered: true } : null;
    }, [templates, props.value, props.selectedLang, preferredLang]);
    const description = (row) => [
        lang.languages[row.shown.lang],
        row.shown.isForAlternativeContact === true
            ? lang.templates.alternativeContact
            : null,
    ]
        .filter(notEmpty)
        .join(' · ');
    return (React.createElement(SearchableSelect
    // A selection whose template is gone keeps its raw id so it can be cleared
    , { 
        // A selection whose template is gone keeps its raw id so it can be cleared
        value: selectedRow ? rowId(selectedRow) : props.value, onChange: (id, row) => props.onChange(row?.shown ?? null, row?.variants ?? []), placeholder: props.placeholder ?? lang.interactions.selectTemplate, disabled: props.disabled, autofocus: props.autofocus, style: props.style, className: props.className, items: rows, getId: rowId, getRecentsKey: rowTemplateId, getLabel: rowLabel, getIcon: languageIcon, getDescription: description, browseFilter: isOffered, facet: facet, selectedItem: selectedRow, labels: {
            search: lang.templates.searchTemplates,
            all: lang.templates.allTemplates,
        }, recentsStorageKey: "debbie.templateSelect.recentlyUsed" }));
};
export default TemplateSelect;
//# sourceMappingURL=TemplateSelect.js.map